import { newId } from "./ids";
import type { SeamHandle } from "./seamGeometry";

export type { SeamHandle };

export type Vec2 = { x: number; y: number };
export type Bbox = { x: number; y: number; w: number; h: number };
export type Mat3 = [number, number, number, number, number, number];

export type DecorationKind =
  | "stamp"
  | "lineStamp"
  | "fill"
  | "offset"
  | "freePath";

export interface BaseDecoration {
  id: string;
  kind: DecorationKind;
  name: string;
  visible: boolean;
  depth: number;
}

export interface StampDecoration extends BaseDecoration {
  kind: "stamp";
  stampId: string;
  position: Vec2;
  rotation: number;
  scale: number;
}

export interface LineStampDecoration extends BaseDecoration {
  kind: "lineStamp";
  stampId: string;
  pathData: string;
  spacing: number;
  scale: number;
  rotateAlong: boolean;
  closed: boolean;
}

export interface FillDecoration extends BaseDecoration {
  kind: "fill";
  stampId: string;
  regionPathData: string;
  rotation: number;
  scale: number;
  edgeMatch: boolean;
}

export interface OffsetDecoration extends BaseDecoration {
  kind: "offset";
  sourcePathData: string;
  distance: number;
  joinType: "miter" | "round" | "bevel";
}

export interface FreePathDecoration extends BaseDecoration {
  kind: "freePath";
  pathData: string;
  strokeWidth: number;
  closed: boolean;
}

export type Decoration =
  | StampDecoration
  | LineStampDecoration
  | FillDecoration
  | OffsetDecoration
  | FreePathDecoration;

export interface Hole {
  id: string;
  position: Vec2;
  radius: number;
  /** Optional — treated as visible when undefined for backward compat. */
  visible?: boolean;
}

export interface Cutout {
  id: string;
  pathData: string;
  /** Per-anchor smooth / angle locks + bezier-cycle snapshots on the
   *  cutout outline — same semantics as the piece fields (#970). */
  smoothAnchors?: ConstructionLine["smoothAnchors"];
  bezierMemory?: ConstructionLine["bezierMemory"];
  /** Rounded corners (#974, epic #781) — the same contract construction lines
   *  use (`ConstructionLine.cornerRounding`): `sharpPathData` is the un-rounded
   *  source of truth and the visible outline is re-derived from it on every
   *  commit, so a radius stays adjustable and a corner can be taken back to
   *  sharp. Corner indices address anchors in `sharpPathData`, so they don't
   *  shift as more corners are rounded.
   *
   *  Cleared by any direct geometry edit (point / handle / curve drag, point
   *  delete), at which point the rounded geometry becomes the new source —
   *  mirroring how construction drops the metadata on its first destructive
   *  edit. */
  cornerRounding?: ConstructionLine["cornerRounding"];
  /** Baked rotation metadata (#971) — same construction-line semantics as
   *  `ConstructionLine.rotation` / `rotationCenter`: the geometry is stored
   *  ROTATED, and these recover the un-rotated frame so a later resize runs
   *  un-skewed and the transform overlay draws tilted. */
  rotation?: number;
  rotationCenter?: Vec2;
  /** User-positioned rotation pivot in project space (#990 follow-up).
   *  The leather twin of `ConstructionLine.pivotPoint` (#420), and stored on
   *  the model for the same reason: a pivot the user parked has to survive
   *  selecting something else, switching tools, and a reload. Unlike
   *  `rotationCenter` — a derived field that drifts as rotations compose —
   *  this is the explicit point the crosshair was dragged to. Absent → the
   *  effective pivot is the displayed bbox center. It moves rigidly with the
   *  geometry (translate / rotate / mirror) and is cleared by a destructive
   *  reshape (resize), for the same reason `rotation` is.  */
  pivotPoint?: Vec2;
  visible?: boolean;
  /** Optional — falls back to "Cutout N" by index when undefined. */
  name?: string;
  /** When set, `pathData` is re-derived from construction geometry on every
   *  commit (#653) — the cutout traces construction lines, like a linked piece. */
  constructionOutline?: ConstructionOutline;
  /** Freehand source samples (#1016): every pointer position from the trace,
   *  kept so the visible geometry can be RE-derived at a different smoothness
   *  later. Present only on freehand-drawn objects, and dropped by the first
   *  destructive geometry edit — the same lifecycle
   *  `ConstructionLine.rawPoints` has (#171). */
  rawPoints?: Vec2[];
  /** Simplify tolerance for {@link rawPoints}: 0 is the raw polyline, higher
   *  fits fewer points and a smoother curve. Present iff `rawPoints` is. */
  smoothness?: number;
  /** Set once the visible geometry has been hand-edited away from the
   *  smoothed trace (#248). {@link rawPoints} is KEPT rather than dropped, so
   *  the Smoothness control stays usable — re-applying it re-derives from the
   *  trace, which discards those hand edits. The derive pass must NOT run on
   *  its own while this is set, or the next commit would silently undo the
   *  edit. Cleared when the smoothness is next applied, since the geometry is
   *  a derived view of the trace again from that point. */
  freehandEdited?: boolean;
}

/** A piece of physical hardware (rivet, snap, eyelet, concho) seated in a
 *  specific hole on a part. The hardware always references an existing
 *  `Hole` (placement either picks a hole the user already drilled or
 *  auto-drills one sized to fit the hardware's post). At most one
 *  hardware item per hole — re-placing on an occupied hole replaces the
 *  existing item. */
export type HardwareType =
  | "rivet"
  | "snap"
  | "magnetic-snap"
  | "eyelet"
  | "concho-round"
  | "concho-star"
  | "concho-floral";

export type HardwareColor =
  | "silver"
  | "brass"
  | "antiqued-brass"
  | "black"
  | "gun-metal";

/** Which half of a two-part hardware piece this entry represents — the
 *  female (socket / top) or male (stud / bottom) side. Only meaningful
 *  for snaps and magnetic snaps; the field is left undefined (and
 *  treated as `female`) for one-piece hardware (rivets, eyelets,
 *  conchos). */
export type HardwareSide = "female" | "male";

export interface Hardware {
  id: string;
  /** id of the `Hole` on the same `Piece` this hardware sits in. */
  holeId: string;
  type: HardwareType;
  color: HardwareColor;
  /** Visible cap diameter in project mm — what the user picks from the
   *  size chips in the panel. The hardware renders centred on the hole
   *  at this diameter; it can (and usually does) overhang the hole's
   *  drilled radius since the cap sits on top of the leather. */
  sizeMm: number;
  /** Female (top / socket) or male (bottom / stud). Only meaningful
   *  when `type` is "snap" or "magnetic-snap"; ignored otherwise.
   *  Optional for backward compatibility — absent is treated as
   *  "female" so older projects still render correctly. */
  side?: HardwareSide;
  visible?: boolean;
}

/** Stitch chisel profile. The hole footprint at each step along the line.
 *  - `round`   — circular pinhole.
 *  - `slant`   — French / oblique slot, slot length is `size`, oriented at
 *                `slantAngle` degrees from the local stitch direction.
 *  - `diamond` — diamond / Japanese pricking, long axis = `size`, short axis
 *                ≈ `size / 3`, oriented along the local stitch direction. */
export type StitchShape = "round" | "slant" | "diamond";

/** Thread metadata associated with a stitching defaults block — purely
 *  informational for now; surfaces in summaries and instructions later. */
export interface StitchingThread {
  /** Approximate thread thickness (mm). 0.5–1.2 covers most leather thread. */
  thickness?: number;
  brand?: string;
  /** CSS color string for the thread (used in instructions / summaries). */
  color?: string;
}

/** How the `pitch` value is interpreted when sampling holes along a seam.
 *  - "center": center-to-center distance between adjacent holes (the
 *              way most chisel manufacturers spec their tools). Default
 *              for round and slanted chisels.
 *  - "gap":    empty space between adjacent holes along the seam. The
 *              actual center-to-center distance ends up `pitch +
 *              hole_seam_length`. Default for diamond chisels — works
 *              well when you want a fixed visual spacing regardless of
 *              tooth size. */
export type StitchPitchMode = "center" | "gap";

/** Shape + spacing + thread parameters that resolve top-down through
 *  project → part → stitch line. Each level can carry the whole block or
 *  omit it to inherit from the next level up. */
export interface StitchingDefaults {
  shape: StitchShape;
  pitch: number;
  size: number;
  slantAngle: number;
  /** How `pitch` is measured. Optional for back-compat with v3 projects
   *  saved before this field existed; absent is treated as "center". */
  pitchMode?: StitchPitchMode;
  thread?: StitchingThread;
}

export interface StitchLine {
  id: string;
  /** Optional — falls back to "Stitch N" by index when undefined. */
  name?: string;
  visible?: boolean;
  /** Polyline vertices in project (mm) coordinates. ≥ 2 points. This is the
   *  seam AS CROPPED to the owning part's leather (#780) — the geometry every
   *  consumer (hole layout, rendering, exporters) reads, so no hole ever lands
   *  off the piece. When the seam was trimmed, {@link authoredPoints} keeps the
   *  full pre-crop geometry so a later move re-crops from it (and moving back
   *  restores). */
  points: Vec2[];
  /** Freehand source samples (#1016): every pointer position from the trace,
   *  kept so the visible geometry can be RE-derived at a different smoothness
   *  later. Present only on freehand-drawn objects, and dropped by the first
   *  destructive geometry edit — the same lifecycle
   *  `ConstructionLine.rawPoints` has (#171). */
  rawPoints?: Vec2[];
  /** Simplify tolerance for {@link rawPoints}: 0 is the raw polyline, higher
   *  fits fewer points and a smoother curve. Present iff `rawPoints` is. */
  smoothness?: number;
  /** Set once the visible geometry has been hand-edited away from the
   *  smoothed trace (#248). {@link rawPoints} is KEPT rather than dropped, so
   *  the Smoothness control stays usable — re-applying it re-derives from the
   *  trace, which discards those hand edits. The derive pass must NOT run on
   *  its own while this is set, or the next commit would silently undo the
   *  edit. Cleared when the smoothness is next applied, since the geometry is
   *  a derived view of the trace again from that point. */
  freehandEdited?: boolean;
  /** Bezier handles for {@link points}, index-parallel and RELATIVE to their
   *  anchor — the same geometry construction lines and piece outlines carry
   *  (#982). Absent (every seam saved before this) means every run is straight,
   *  so old projects load unchanged. Stored relative because that's what keeps
   *  transforms honest: a translation leaves them alone, a rotation turns the
   *  vectors, a resize scales them — the anchors can't move out from under a
   *  curve. See `src/model/seamGeometry.ts`. */
  handles?: SeamHandle[];
  /** Per-anchor smooth / angle locks + bezier-cycle snapshots, the same fields
   *  the piece and cutout outlines carry (#970) — meaningful on a seam only now
   *  that seams have curves to lock (#982). */
  smoothAnchors?: ConstructionLine["smoothAnchors"];
  bezierMemory?: ConstructionLine["bezierMemory"];
  /** The FULL authored seam (#780), before cropping to the part outline — same
   *  coordinate space as {@link points}, moved rigidly with the seam / part.
   *  Present ONLY when the seam overshoots the leather (i.e. `points` is a
   *  strict crop of it); absent when the seam lies wholly inside, in which case
   *  `points` IS the full geometry. Lets a MOVE re-derive the crop at the new
   *  position from the intact seam rather than the already-trimmed points — so
   *  overshoot that comes back over the leather is restored, not lost. Never set
   *  on construction-derived (they clip per-part) or matched seams (#199, which
   *  intentionally span parts). */
  authoredPoints?: Vec2[];
  /** Whether {@link authoredPoints} is a closed ring (#780). The cropped
   *  `points` may be an open arc even when the authored seam was a closed loop
   *  (a shape seam dragged partly off the part), so the authored closed-ness is
   *  tracked separately to re-crop correctly on move. Absent = open. */
  authoredClosed?: boolean;
  /** Handles for {@link authoredPoints} (#982), so a move that brings a trimmed
   *  CURVED seam back over the leather re-crops the real curve rather than the
   *  straightened sample of it. */
  authoredHandles?: SeamHandle[];
  /** Center-to-center distance between holes in mm (chisel pitch / SPI).
   *  Always present so legacy serialized lines without resolution metadata
   *  still render. Cascade-on-write keeps it in sync with the part / project
   *  defaults when the line isn't overriding. */
  pitch: number;
  /** Hole footprint dimension (mm). Diameter for round; long axis for diamond
   *  and tooth-width (perpendicular to seam) for slant. */
  size: number;
  shape: StitchShape;
  /** Degrees between the slant slot's long axis and the local stitch
   *  direction. Only meaningful for `shape === "slant"`. ~45 = traditional
   *  French slant. */
  slantAngle: number;
  /** Distance from the first vertex (in mm) where the first hole is placed.
   *  Lets the user nudge the row forward/back along the seam without
   *  redrawing it. Defaults to 0. */
  startOffset?: number;
  /** How this line's `pitch` is measured. Optional / inherited from the
   *  resolved cascade defaults; `undefined` is treated as "center". */
  pitchMode?: StitchPitchMode;
  /** When true, the line ignores the part / project defaults and uses its
   *  stored shape/pitch/size/slantAngle as-is. When false (the default for
   *  newly-drawn lines), the line follows the resolved defaults — the
   *  cascade keeps its stored fields in sync with whatever level is the
   *  source of truth. Missing on legacy lines is treated as `true` so their
   *  pre-existing values stay editable. */
  overridesDefaults?: boolean;
  /** When true, the seam closes back to the first vertex — i.e., it forms a
   *  loop. The closing segment is implicit (not stored in `points`), so a
   *  triangle is `points = [A, B, C]` with `closed = true`. Optional /
   *  defaults to false so older serialized lines stay open. */
  closed?: boolean;
  /** Baked rotation metadata (#971) — same construction-line semantics as
   *  `ConstructionLine.rotation` / `rotationCenter`: `points` (+
   *  `authoredPoints`) are stored ROTATED, and these recover the un-rotated
   *  frame so a later resize runs un-skewed and the overlay draws tilted.
   *  `slantAngle` is relative to the local seam direction, which rotates with
   *  the points, so it needs no adjustment. */
  rotation?: number;
  rotationCenter?: Vec2;
  /** User-positioned rotation pivot in project space (#990 follow-up).
   *  The leather twin of `ConstructionLine.pivotPoint` (#420), and stored on
   *  the model for the same reason: a pivot the user parked has to survive
   *  selecting something else, switching tools, and a reload. Unlike
   *  `rotationCenter` — a derived field that drifts as rotations compose —
   *  this is the explicit point the crosshair was dragged to. Absent → the
   *  effective pivot is the displayed bbox center. It moves rigidly with the
   *  geometry (translate / rotate / mirror) and is cleared by a destructive
   *  reshape (resize), for the same reason `rotation` is.  */
  pivotPoint?: Vec2;
  /** When set, `points` (and `closed`) are re-derived from construction
   *  geometry on every commit (#653) — the seam traces construction lines. */
  constructionOutline?: ConstructionOutline;
  /** DERIVED (#199 Phase 3): for a pitch-based seam traced from a construction
   *  line, the local arc offset (in `[0, pitch)`) of the first hole so the row
   *  lands on a grid anchored to the SOURCE construction line's own origin. Two
   *  seams tracing the same line at the same pitch then place holes at the same
   *  points → they line up hole-for-hole where they overlap. Set by
   *  `deriveConstructionStitchGrid`; `undefined` = the normal (un-anchored) row. */
  gridPhase?: number;
  /** DERIVED (#1107): when the group's leader lays its row out with the
   *  corner-aware layout, its holes are NOT on a `pitch` grid, so a follower
   *  can't reach them with `gridPhase`. Instead it carries the leader's actual
   *  holes that fall on its span, as local arc offsets measured from
   *  `points[0]` (raw, before `direction`), ascending. Set by
   *  `deriveConstructionStitchGrid`; `undefined` = place on the grid as
   *  usual. */
  gridHoles?: number[];
  /** DERIVED (#1105): the subset of `gridHoles` that are the leader's PIVOT
   *  holes (the ones its corner layout pinned on a corner and punches round,
   *  `StitchHolePlacement.corner`), so the follower punches its partner holes
   *  round too. Same offsets as `gridHoles`; absent when there are none. */
  gridCornerHoles?: number[];
  /** DERIVED (#199 Phase 3): true on the ONE seam in a grid group that leads it
   *  — the longest seam, whose Start handle anchors the shared grid. Every other
   *  member of the group has `gridPhase` set but not this. Set by
   *  `deriveConstructionStitchGrid`; lets the UI label which seam defines the
   *  shared hole positions ("Grid leader") vs. which follow it ("Grid follower").
   *  `undefined` on followers and on un-anchored seams. */
  gridMain?: boolean;
  /** Fixed number of holes spread EVENLY along the seam, overriding the
   *  pitch-based sampling, so two seams of different length can share a count
   *  (#199 stitch matching). Derived from the seam's `StitchMatch`, not edited
   *  directly — `deriveStitchMatches` stamps it on matched lines and clears it
   *  on break. `undefined` = the normal pitch-based row. */
  holeCount?: number;
  /** Back-ref to the `Project.stitchMatches` record this seam belongs to
   *  (#199). Maintained by `deriveStitchMatches`; lets the panel / layers find
   *  the partner without scanning every match. */
  matchId?: string;
  /** On a matched FOLLOWER, whether the row is thinned by skipping stitches
   *  (#199). The boolean gate — read this to detect IF a seam skips at all (e.g.
   *  for instructions); read `skipEvery` for the actual N. Must be explicitly
   *  `true` for any skipping to apply; `false` / absent keeps the last
   *  `skipEvery` on the line but skips nothing. See `effectiveSkipEvery`. */
  skipStitches?: boolean;
  /** On a matched FOLLOWER, omit every Nth hole from the otherwise full grid
   *  (#199) — `2` skips every 2nd hole, etc. Ranges `2 … floor(holeCount / 2)`.
   *  Only applied when `skipStitches` is on; use `effectiveSkipEvery(line)` to
   *  read the value that's actually in effect. */
  skipEvery?: number;
  /** On a MAIN whose follower uses `skipEvery` (#687): the same cadence, stamped
   *  by `deriveStitchMatches` so the main can MARK every Nth hole (a slash) as
   *  the one to skip when sewing — the main keeps all its holes, but those marked
   *  ones have no partner on the (fewer-holed) follower. `undefined` / `< 2` =
   *  nothing to mark. */
  skipMarkEvery?: number;
  /** Working-area insets (mm) — the holes are placed only within
   *  `[startInset, length - endInset]` along the seam, so the row keeps a margin
   *  from each end. The two heads of the panel's working-area range. When unset,
   *  each defaults to `min(pitch / 2, 1/8")` on an OPEN seam and `0` on a CLOSED
   *  loop (continuous all the way around). Adjustable 0 … seam length. */
  startInset?: number;
  endInset?: number;
  /** On a matched FOLLOWER (#199): the main's effective center-to-center pitch,
   *  inherited so the follower can lay its holes at the SAME pitch as the main
   *  (equal-length seams then line up hole-for-hole, and the follower stays put
   *  when the main's working-area length changes). Derived by
   *  `deriveStitchMatches`; consulted only when `fillWorkingArea` is not set. */
  matchSpacing?: number;
  /** On a matched FOLLOWER (#199): user opt-out of "Match pitch exactly". When
   *  set (checkbox unchecked), the follower spreads its count EVENLY across its
   *  own working area instead of mirroring the main's pitch. Absent (default) =
   *  match the main's pitch via `matchSpacing`. */
  fillWorkingArea?: boolean;
  /** On a matched FOLLOWER with a slant / diamond chisel (#199): override the
   *  inherited slant DIRECTION while keeping the main's shape + size. `false` =
   *  Regular (slant leans negative), `true` = Reverse. `undefined` = inherit the
   *  main's direction. Applied by `deriveStitchMatches`. */
  slantReverse?: boolean;
  /** Which way the seam is walked when placing holes (#796). `"cw"` (the
   *  default) runs from the first vertex forward; `"ccw"` runs the other way —
   *  the working-area start/end swap and the slant tangents reverse. Purely a
   *  traversal flag: the stored `points` stay canonical, so a mirror keeps the
   *  reflected geometry and just flips this so the row's start/end stay
   *  consistent (the seam doesn't silently keep going the old way). Absent =
   *  `"cw"`. */
  direction?: "cw" | "ccw";
  /** Advanced layout (#691): lay the row out CORNER-FIRST instead of marching at
   *  exact pitch — a hole pinned on every corner and on both ends of the working
   *  area, each run between pins divided into the whole number of gaps closest
   *  to the pitch, and a run too short to hold a gap losing its corner pin. The
   *  trade is explicit: spacing is within a fraction of the chisel pitch rather
   *  than exactly it, in exchange for symmetric corners and no sliver at the
   *  close. Absent / false = the ordinary exact-pitch walk. Applies to
   *  pitch-driven seams; a matched seam's count comes from its partner, so it
   *  keeps the count-based row. See `src/model/stitchLayout.ts`. */
  cornerLayout?: boolean;
  /** Advanced layout (#691): how sharp a bend has to be to count as a corner,
   *  as the INTERIOR angle in degrees — 180 is straight, 90 is square. An
   *  anchor is a corner when its angle is BELOW this, so the value itself is
   *  the widest angle still treated as a smooth bend. Absent = the 120°
   *  default. Governs both the corner pins and the round corner holes, since
   *  both ask the same question. See `src/model/stitchLayout.ts`. */
  cornerMaxSmoothAngle?: number;
  /** Advanced layout (#691): the shortest gap this seam will punch, as a
   *  fraction of its pitch — 1 is a full stitch, 0 turns the rule off. Governs
   *  both places a hole gets dropped: a run's leftover too short to punch, and
   *  a corner pin crowding the pin before it. Absent = 0.55, just past half a
   *  pitch. How tight is too tight depends on the leather and the thread as
   *  much as on the iron, which is why it is a per-seam setting. */
  minGapFraction?: number;
  /** Advanced layout (#691): `false` to let the seam's own ends take a share of
   *  the leftover like a pivot hole does. ABSENT MEANS THEY DON'T — the row
   *  starts and finishes at exactly the chisel pitch, and a run's leftover goes
   *  entirely to the gaps beside its pivot holes. That is the default because a
   *  seam usually has to meet something at each end, and only the opt-out is
   *  stored. Cleared when the corner layout is switched off. Meaningless on a
   *  loop, which has no ends, and not offered there. */
  noEndSpacing?: boolean;
  /** Advanced layout (#691): `false` to punch the pivot holes with the row's
   *  own chisel instead of round. ABSENT MEANS ROUND — it is the reason to want
   *  pivot holes at all, so it is what you get by default, and only an explicit
   *  opt-out is stored. Cleared when the corner layout is switched off, so
   *  turning it back on starts from the default rather than from a preference
   *  set during some earlier session.
   *
   *  Punch the hole at each corner ROUND, whatever
   *  chisel the rest of the row uses — what a leatherworker does with an awl,
   *  because a slanted or diamond slot at the turn points the wrong way for the
   *  thread and is the corner most likely to tear. Independent of
   *  {@link cornerLayout}: with it on a hole is pinned exactly on each corner
   *  and that one is rounded; with it off no hole lands on a corner, so the
   *  hole NEAREST each corner is rounded instead. Meaningless — and not
   *  offered — on a seam already punched round throughout. */
  roundCornerHoles?: boolean;
}

/** Optional parametric description of a construction line drawn from
 *  one of the tool's primitive kinds (rect / ellipse / slot). Lets
 *  the properties panel show editable dimension inputs and the
 *  layers panel name the line by its shape. Cleared the moment the
 *  user edits the line non-parametrically (drag a vertex, bend a
 *  curve, drag a tangent handle) — at that point the geometry is no
 *  longer the simple primitive these params describe. */
export type ConstructionPrimitive =
  | {
      kind: "rectangle";
      /** Top-left in project mm. */
      x: number;
      y: number;
      width: number;
      height: number;
    }
  | {
      kind: "ellipse";
      /** Top-left of the bounding box in project mm. */
      x: number;
      y: number;
      width: number;
      height: number;
    }
  | {
      kind: "pill";
      /** Capsule axis endpoints in project mm. */
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      /** Short-axis width (capsule diameter perpendicular to the
       *  axis). The capsule's overall length is `dist(p1, p2) + width`. */
      width: number;
    }
  | {
      kind: "line";
      /** Endpoints in project mm. The line is a single open segment
       *  from (x1, y1) to (x2, y2). Length = dist(p1, p2), angle =
       *  atan2(y2-y1, x2-x1). */
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    };

/** A guide / reference shape — not part of the cut pattern, just a
 *  visual aid for laying out the design. Stored as SVG path data so the
 *  same type covers polylines, polygons, rectangles, ellipses, slots,
 *  and freehand strokes; the renderer always paints them as a thin
 *  dashed outline regardless of source shape. */
export interface ConstructionLine {
  id: string;
  /** Optional — falls back to "Construction N" by index when undefined. */
  name?: string;
  visible?: boolean;
  /** SVG path data. May be open (no trailing `Z`) for polylines /
   *  freehand strokes, or closed (`Z`) for polygons / rect / ellipse /
   *  slot. */
  pathData: string;
  /** Anchors with a tangent-handle constraint applied. Three states
   *  are possible per anchor:
   *   - absent from the array → "independent" (handles fully free)
   *   - present with `mode: "angle"` → handles stay collinear (180°
   *     apart) but can have different magnitudes
   *   - present with `mode: "smooth"` (or no mode for back-compat)
   *     → handles fully mirrored (collinear AND equal magnitude)
   *  Toggled via the smooth-toggle sub-sub-tool / dblclick on an
   *  anchor in the Construction tool's Edit sub-mode (cycles
   *  independent → angle → smooth → independent). Stored as
   *  `(pathIndex, segmentIndex)` tuples to address segments inside
   *  compound paths. Field name kept as `smoothAnchors` for
   *  backward compat with serialized projects. */
  smoothAnchors?: Array<{
    pathIndex: number;
    segmentIndex: number;
    mode?: "angle" | "smooth";
  }>;
  /** Set when the line was drawn from a parametric primitive
   *  (rect / ellipse / slot) and hasn't been edited away from that
   *  shape since. Drives the modified name (e.g. "Construction -
   *  Square 1") and the editable dimension inputs in the properties
   *  panel. */
  primitive?: ConstructionPrimitive;
  /** Snapshot of the parametric primitive saved when a *reversible*
   *  edit (the bezier-cycle anchor toggle) drops the primitive. Lets
   *  a subsequent cycle edit that returns the geometry to the
   *  primitive's natural shape restore the primitive identity.
   *  Cleared by any *destructive* edit (vertex / curve / handle
   *  drag) — at that point the line is permanently a polygon.
   *  `name` is the auto-generated label the line carried at the
   *  moment of conversion (e.g., "Construction - Square 1"); it
   *  gets restored alongside the primitive so a round-trip leaves
   *  no naming residue. Absent when the line had a hand-typed name
   *  (those are preserved as-is throughout). */
  convertedFromPrimitive?: {
    primitive: ConstructionPrimitive;
    name?: string;
  };
  /** Records how the line was authored (polyline / polygon / freehand)
   *  when there's no parametric primitive. Drives the panel's header
   *  label ("Polyline" / "Polygon" / "Curve") and the polyline /
   *  polygon segment list. Persists across non-parametric edits so
   *  the label sticks. */
  kind?: "polyline" | "polygon" | "freehand";
  /** Snapshot of the bezier handle offsets (relative to the
   *  anchor) for any segment that the user toggled back to a sharp
   *  corner via the bezier cycle. When the same anchor's handles
   *  are re-added later (state 0 → state 1) the saved offsets are
   *  restored instead of bootstrapping a fresh tangent, so the
   *  user's previous curve shape is preserved across off / on
   *  toggles.
   *
   *  `preLock` (when present) holds the original independent (state
   *  1) handles captured at the moment the user first applied a
   *  lock (1 → 2), and is preserved across the lock chain only as
   *  long as the user hasn't manually moved the handles since. On
   *  0 → 1 restore we prefer `preLock` over the post-lock snapshot
   *  so the cycle "comes back" to the user's hand-tuned independent
   *  shape rather than the algorithmic lock geometry. */
  bezierMemory?: Array<{
    pathIndex: number;
    segmentIndex: number;
    handleIn: { x: number; y: number };
    handleOut: { x: number; y: number };
    preLock?: {
      handleIn: { x: number; y: number };
      handleOut: { x: number; y: number };
    };
    /** State-2 (angle-only) handle snapshot captured at the moment
     *  of 2 → 3, preserved while no manual drag has happened in
     *  state 3. When the cycle next reaches 1 → 2 we restore from
     *  this snapshot instead of running the algorithmic formula —
     *  preserving the user's chosen angle-only magnitudes across a
     *  3 → 0 → 1 → 2 round trip. */
    preMirror?: {
      handleIn: { x: number; y: number };
      handleOut: { x: number; y: number };
    };
  }>;
  /** Present on derived (offset) lines (issue #29). The line's
   *  `pathData` is regenerated every commit from the source line
   *  expanded outward (`distance > 0`) or inward (`distance < 0`)
   *  by `distance` mm. Derived lines cannot be edited directly —
   *  every vertex / handle / curve / segment / whole-line drag
   *  refuses when this field is set. To change one, the user edits
   *  the source or types a new distance in the properties panel.
   *  Pruned if the source line is deleted. */
  offsetSource?: {
    /** `id` of the source ConstructionLine the offset follows. */
    sourceId: string;
    /** Offset distance in project mm. Positive = expand (outward
     *  from a closed shape, or the user's chosen perpendicular for
     *  open paths); negative = the opposite side. */
    distance: number;
    /** Opt-OUT: by default, an outward offset of a sharp source
     *  (rectangle, polygon) emits arcs of radius `distance` at
     *  every corner — matching the geometric meaning of "expand
     *  outward by a uniform distance". Setting this to `false`
     *  flips back to sharp corners. The default-on semantics are
     *  encoded by checking `roundedCorners !== false` at the use
     *  sites, so legacy projects (and freshly-created offsets that
     *  don't set the field) render rounded. Has no effect on
     *  inward offsets (the rounded-inward shape isn't well-defined
     *  for a sharp source corner) or on sources that already
     *  carry their own corner-rounding metadata. */
    roundedCorners?: boolean;
    /** Single-segment offset (issue #681). When present, the offset
     *  follows just ONE edge of the source instead of the whole
     *  outline: the deriver emits a single straight line parallel to
     *  `source.pathData`'s `subpaths[pathIndex].curves[curveIndex]`,
     *  perpendicular-offset by `distance`. Absent = today's
     *  whole-shape offset (unchanged). The edge is tracked by index,
     *  so adding/removing source vertices can shift which edge is
     *  followed — the same re-derive tradeoff as the whole-line
     *  offset. `roundedCorners` has no meaning for a single edge. */
    segment?: {
      /** Subpath index within the source's CompoundPath. */
      pathIndex: number;
      /** Curve (edge) index within that subpath. */
      curveIndex: number;
    };
    /** Source edges this offset SKIPS (issue #116). A whole-outline
     *  offset normally follows every edge of the source; tapping a
     *  stretch of it with the Delete sub-tool adds that stretch's
     *  generating edge here, and the deriver trims the matching run
     *  out of the offset geometry. Absent / empty = follow the whole
     *  outline (the default, and byte-identical to pre-#116 output).
     *  Edges are addressed the same way `segment` addresses its one
     *  edge, and carry the same re-derive tradeoff: adding or removing
     *  source vertices shifts which edges the indices name.
     *
     *  A trimmed offset is no longer a parametric shape, so the
     *  deriver drops the heuristic primitive / kind / corner-rounding
     *  metadata for as long as this list is non-empty. Meaningless
     *  alongside `segment` (that offset IS a single edge — deleting it
     *  deletes the line), so the two never coexist. */
    excludedSegments?: Array<{
      pathIndex: number;
      curveIndex: number;
    }>;
  };
  /** Present on derived (mirror) lines (issue #180). The line's
   *  `pathData` is regenerated every commit by reflecting the source
   *  line across the axis described in `axis`. Like `offsetSource`,
   *  derived mirror lines refuse direct geometry edits — the user
   *  edits the source (or moves the axis) to change them, and the
   *  derived line follows on the next commit. Pruned when either the
   *  source line OR the axis line is deleted; if the user wants to
   *  keep the mirrored geometry standalone, the "Detach" affordance
   *  on the layers-panel chip / properties-panel row drops the
   *  `mirrorSource` field so the line stops re-deriving. */
  mirrorSource?: {
    /** `id` of the source ConstructionLine the mirror follows. */
    sourceId: string;
    /** Axis to mirror across. Two flavours:
     *   - `segment`: reflect across the infinite line containing the
     *     segment from anchor `(pathIndex, segmentIndex)` to its
     *     successor (or to anchor 0 when the axis line is closed and
     *     the addressed anchor is the last one).
     *   - `point`: 180° rotation about anchor `(pathIndex,
     *     segmentIndex)`. Mathematically a "point mirror" / central
     *     inversion. */
    axis:
      | {
          kind: "segment";
          constructionId: string;
          pathIndex: number;
          segmentIndex: number;
        }
      | {
          kind: "point";
          constructionId: string;
          pathIndex: number;
          segmentIndex: number;
        };
    /** Extra separation (mm) pushed along the reflection's own direction, for a
     *  copy mirrored across the SOURCE'S OWN edge (#343). Across another line's
     *  axis the copy butts against it and this is absent; across your own, the
     *  reflection would land flush on top of the original, so the "mirror that
     *  way" gesture sets this to the cursor's distance from the axis — which
     *  puts the copy's leading edge under the pointer.
     *
     *  It has to be STORED rather than baked into the geometry: the mirror is a
     *  live derived line, so the gap must survive every re-derive and travel
     *  with the pair when the source moves. */
    gapMm?: number;
  };
  /** Per-corner radii applied by the Round sub-tool (issue #100).
   *  When present, `sharpPathData` holds the un-rounded source
   *  geometry and `pathData` holds the result of running
   *  `applyCornerRounding` over `sharpPathData` with `corners`.
   *  The commit pipeline re-derives `pathData` on every commit so
   *  any operation that changes `sharpPathData` (e.g. dragging the
   *  whole line) keeps the rounded view in sync.
   *
   *  Corner indices refer to anchors in `sharpPathData` — they do
   *  not shift as more corners get rounded, because the rounded
   *  result is derived fresh each pass from the sharp source.
   *
   *  Cleared by any direct geometry edit (vertex / handle / curve
   *  drag, segment delete) — at that point the rounded pathData
   *  becomes the new source and the rounding metadata is dropped,
   *  mirroring how `primitive` is dropped on the first destructive
   *  edit. */
  cornerRounding?: {
    sharpPathData: string;
    corners: Array<{
      pathIndex: number;
      segmentIndex: number;
      radius: number;
    }>;
  };
  /** Accumulated rotation applied to the line via the Select sub-mode's
   *  rotation handle (issue #67). The line's `pathData` already
   *  contains the rotated geometry — this field is informational: it
   *  drives the bbox overlay (rendered as a tilted rectangle) and the
   *  Rotation scrubber in the properties panel so the user can read
   *  / scrub back to 0° without losing the original orientation.
   *  Normalised to (-180, 180]. Undefined / absent → no rotation
   *  applied yet (legacy / freshly-drawn lines). Cleared / reset to
   *  the new orientation on any destructive reshape (vertex drag,
   *  primitive resize, etc.) because the "natural" frame of
   *  reference is the post-edit shape from that point onward. */
  rotation?: number;
  /** Project-space pivot the rotation was applied about. Used to
   *  reconstruct the un-rotated bbox at render time (clone pathData,
   *  rotate by `-rotation` about this point, take bbox, draw it
   *  rotated by `+rotation` about this point). Set whenever
   *  `rotation` is set; absent when `rotation` is 0 / undefined. */
  rotationCenter?: Vec2;
  /** User-positioned rotation pivot in project space (issue #420).
   *  Unlike `rotationCenter` (a derived/composed display field that
   *  drifts after each rotation), this is the explicit point the
   *  user dragged the pivot crosshair to. Absent → the effective
   *  pivot defaults to the displayed bbox centre. It is NOT
   *  recomputed by `composeRotations`; it only moves rigidly with
   *  the shape (translate / mirror / flip) and is cleared on any
   *  destructive reshape (vertex drag, primitive resize, etc.) for
   *  the same reason `rotation` is. */
  pivotPoint?: Vec2;
  /** Raw pointer samples captured during a freehand draw (issue
   *  #171). Source of truth while present — `pathData` is re-derived
   *  from this + `smoothness` on every commit by
   *  `deriveFreehandSmoothing`. Set only on `kind === "freehand"`
   *  lines, immediately after a freehand draw. Dropped on the first
   *  destructive geometry edit (same lifecycle as `primitive` /
   *  `cornerRounding`) — after that the user is editing the bezier
   *  curve directly and the Smoothness slider no longer applies.
   *  Undo restores the pre-edit snapshot, which still has both
   *  fields. */
  rawPoints?: Vec2[];
  /** Paper.js `path.simplify(tolerance)` value. 0 = no
   *  simplification (visible path is the raw polyline); higher =
   *  fewer points, smoother curve. Present iff `rawPoints` is. */
  smoothness?: number;
  /** Set once the visible geometry has been hand-edited away from the
   *  smoothed trace (#248). {@link rawPoints} is KEPT rather than dropped, so
   *  the Smoothness control stays usable — re-applying it re-derives from the
   *  trace, which discards those hand edits. The derive pass must NOT run on
   *  its own while this is set, or the next commit would silently undo the
   *  edit. Cleared when the smoothness is next applied, since the geometry is
   *  a derived view of the trace again from that point. */
  freehandEdited?: boolean;
}

/** A named, user-created grouping of construction lines. Groups can
 *  nest — a `ConstructionGroup` holds direct `lineIds` and optional
 *  child `groupIds`, building a tree. Each line is a direct member of
 *  at most one group; each group is a child of at most one parent
 *  group. Cycles are forbidden (a group cannot be its own ancestor).
 *  Groups with no members and no children get pruned by the helpers
 *  in `projectOps.ts` so the model never persists an empty record. */
export interface ConstructionGroup {
  id: string;
  name: string;
  lineIds: string[];
  /** Child groups in display order. Absent on legacy records — treat
   *  undefined as an empty array. */
  groupIds?: string[];
  /** Tracing images (issue #509) that are direct members of this
   *  group, in display order. Images are leaf members like lines —
   *  they never own children — so they ride along when the group
   *  moves and nest under the group in the layers panel. Absent on
   *  records saved before images could be grouped; treat undefined as
   *  an empty array. */
  imageIds?: string[];
  /** Accumulated rotation applied to the group via the Select sub-
   *  mode's rotation gesture (issue #67 follow-up). The members'
   *  `pathData` already contains the rotated geometry — this field
   *  drives the group's bbox overlay so the dashed frame stays the
   *  un-rotated size rotated by this angle (rather than spawning a
   *  fresh axis-aligned bbox around the rotated cluster on each
   *  commit). Normalised to (-180, 180]. Cleared when group
   *  membership changes (members added / removed via grouping) so
   *  the bbox snapshot doesn't outlive its reference geometry. */
  rotation?: number;
  /** Project-space pivot the rotation was applied about. Used to
   *  reconstruct the un-rotated bbox at render time (un-rotate
   *  every member by `-rotation` about this point, union the
   *  bboxes, draw the union rotated by `+rotation` about this
   *  point). Set whenever `rotation` is set; absent otherwise. */
  rotationCenter?: Vec2;
  /** User-positioned rotation pivot in project space (issue #420).
   *  Group analogue of `ConstructionLine.pivotPoint`: the explicit
   *  point the user dragged the pivot crosshair to. Absent → the
   *  effective pivot defaults to the displayed union-bbox centre.
   *  Moves rigidly with the group (translate / mirror / flip);
   *  cleared when group membership changes. */
  pivotPoint?: Vec2;
}

/** Endpoint of an outline segment (#37). Anchors the leg onto stable
 *  construction geometry so the magnetic link survives line edits:
 *  - `anchor`      — a specific vertex of a construction line.
 *  - `intersection`— where two construction lines cross; `hint` picks
 *                    which crossing when the pair intersects more than
 *                    once (nearest-to-hint wins).
 *  - `along`       — a parametric position (0..1 of arc length) on a
 *                    subpath; the loosest ref, used only as a fallback. */
export type OutlineEndpoint =
  | {
      kind: "anchor";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    }
  | { kind: "intersection"; aId: string; bId: string; hint?: Vec2 }
  | { kind: "along"; constructionId: string; pathIndex: number; t: number };

/** One leg of a magnetic outline: follow `sourceId`'s subpath from
 *  `start` to `end`. `reversed` selects the arc when the source is a
 *  closed line (the two endpoints split a loop into two arcs). */
export interface OutlineSegmentRef {
  sourceId: string;
  pathIndex: number;
  start: OutlineEndpoint;
  end: OutlineEndpoint;
  reversed?: boolean;
}

/** A part / cutout / stitch outline traced from construction segments
 *  and re-derived on every commit (#37). The geometry the outline
 *  produces is written back to the host entity's own field
 *  (`Piece.shapePathData`, etc.) in the assembled frame; `layout` is an
 *  optional cut-layout transform applied at render/export time (#652). */
export interface ConstructionOutline {
  segments: OutlineSegmentRef[];
  closed: boolean;
  layout?: {
    dx: number;
    dy: number;
    rotation?: number;
    cx?: number;
    cy?: number;
  };
}

export interface Piece {
  id: string;
  name: string;
  shapePathData: string;
  /** Per-anchor smooth / angle locks on the outline (#969 parity with
   *  construction's `smoothAnchors`): an entry marks the anchor's
   *  bezier-cycle lock, constraining tangent edits to stay mirrored
   *  (smooth) or collinear (angle). Absent anchors are unlocked. */
  smoothAnchors?: ConstructionLine["smoothAnchors"];
  /** Bezier-cycle handle snapshots for the outline's anchors — same
   *  role as construction's `bezierMemory` (restore handles when the
   *  cycle returns to a previous state). */
  bezierMemory?: ConstructionLine["bezierMemory"];
  /** Rounded corners (#974, epic #781) — the same contract construction lines
   *  use (`ConstructionLine.cornerRounding`): `sharpPathData` is the un-rounded
   *  source of truth and the visible outline is re-derived from it on every
   *  commit, so a radius stays adjustable and a corner can be taken back to
   *  sharp. Corner indices address anchors in `sharpPathData`, so they don't
   *  shift as more corners are rounded.
   *
   *  Cleared by any direct geometry edit (point / handle / curve drag, point
   *  delete), at which point the rounded geometry becomes the new source —
   *  mirroring how construction drops the metadata on its first destructive
   *  edit. */
  cornerRounding?: ConstructionLine["cornerRounding"];
  /** Baked rotation metadata (#971) — same construction-line semantics as
   *  `ConstructionLine.rotation` / `rotationCenter`: `shapePathData` (and
   *  holes / cutouts / stitch lines / decorations) are stored ROTATED, and
   *  these recover the un-rotated frame so a later resize runs un-skewed and
   *  the on-canvas transform overlay draws tilted. */
  rotation?: number;
  rotationCenter?: Vec2;
  /** User-positioned rotation pivot in project space (#990 follow-up).
   *  The leather twin of `ConstructionLine.pivotPoint` (#420), and stored on
   *  the model for the same reason: a pivot the user parked has to survive
   *  selecting something else, switching tools, and a reload. Unlike
   *  `rotationCenter` — a derived field that drifts as rotations compose —
   *  this is the explicit point the crosshair was dragged to. Absent → the
   *  effective pivot is the displayed bbox center. It moves rigidly with the
   *  geometry (translate / rotate / mirror) and is cleared by a destructive
   *  reshape (resize), for the same reason `rotation` is.  */
  pivotPoint?: Vec2;
  /** Freehand source samples (#1016): every pointer position from the trace,
   *  kept so the visible geometry can be RE-derived at a different smoothness
   *  later. Present only on freehand-drawn objects, and dropped by the first
   *  destructive geometry edit — the same lifecycle
   *  `ConstructionLine.rawPoints` has (#171). */
  rawPoints?: Vec2[];
  /** Simplify tolerance for {@link rawPoints}: 0 is the raw polyline, higher
   *  fits fewer points and a smoother curve. Present iff `rawPoints` is. */
  smoothness?: number;
  /** Set once the visible geometry has been hand-edited away from the
   *  smoothed trace (#248). {@link rawPoints} is KEPT rather than dropped, so
   *  the Smoothness control stays usable — re-applying it re-derives from the
   *  trace, which discards those hand edits. The derive pass must NOT run on
   *  its own while this is set, or the next commit would silently undo the
   *  edit. Cleared when the smoothness is next applied, since the geometry is
   *  a derived view of the trace again from that point. */
  freehandEdited?: boolean;
  /** When set, `shapePathData` is re-derived from construction geometry
   *  on every commit (#37); the outline is shape-locked (edit the source
   *  lines, not the piece). */
  constructionOutline?: ConstructionOutline;
  holes: Hole[];
  cutouts: Cutout[];
  /** Optional for backward compatibility — older projects loaded from JSON
   *  may not have this field; treat absent as empty. */
  stitchLines?: StitchLine[];
  /** Optional override of the project's stitching defaults. When omitted,
   *  this part inherits the project-level block. */
  stitchingDefaults?: StitchingDefaults;
  decorations: Decoration[];
  /** Optional for backward compatibility — projects from before the
   *  Hardware tool shipped have no `hardware` field; treat absent as
   *  empty. Each entry references an existing `Hole.id` on this part. */
  hardware?: Hardware[];
  visible?: boolean;
}

/** Treats `undefined` as visible so projects saved before the visibility flag
 *  was added still render correctly. */
export function isVisible(item: { visible?: boolean }): boolean {
  return item.visible !== false;
}

export interface UserStamp {
  id: string;
  name: string;
  svg: string;
}

export interface ProjectSettings {
  units: "mm" | "in";
  background: "dark" | "leather" | "light";
}

/** A constraint is a geometric relationship between two (or more)
 *  features — anchors, lines, or sub-curves. Per issue #30 the
 *  initial set covers ten relationships (coincident through
 *  symmetry). Constraints are stored as project-level metadata so
 *  multiple lines can participate in the same constraint without
 *  one of them "owning" it. */
export type ConstraintKind =
  | "coincident"
  | "on-line"
  | "perpendicular"
  | "tangent"
  | "equal"
  | "parallel"
  | "midpoint"
  | "concentric"
  | "collinear"
  | "symmetry";

/** Reference to a geometric feature a constraint applies to. The
 *  three shapes mirror the hit-test shapes the Construction tool
 *  already uses, so existing selection / hit-testing code can
 *  produce them directly. */
export type ConstraintRef =
  | { kind: "line"; constructionId: string }
  | {
      kind: "anchor";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    }
  | {
      kind: "curve";
      constructionId: string;
      pathIndex: number;
      curveIndex: number;
    }
  /** The bbox centre of a parametric primitive (rect / ellipse /
   *  pill). Behaves like a virtual anchor: snap pins it, chain
   *  translation moves the whole owning line so its centre tracks
   *  the partner. Becomes meaningless if the line ever loses its
   *  primitive — callers prune any constraint referencing such a
   *  line on commit. */
  | { kind: "center"; constructionId: string }
  /** The midpoint of one of the 4 bbox edges of a parametric
   *  primitive (rect / ellipse / pill). Same lifecycle as `center` —
   *  only valid while the target line still has a primitive; pruned
   *  on commit when the primitive drops. `edge` picks which side's
   *  midpoint the ref addresses. */
  | {
      kind: "bbox-midpoint";
      constructionId: string;
      edge: "top" | "right" | "bottom" | "left";
    };

export interface Constraint {
  id: string;
  kind: ConstraintKind;
  /** Two-or-more features participating in the relationship. The
   *  count and accepted ref kinds depend on `kind` — coincident
   *  takes two anchors, parallel takes two lines, symmetry takes
   *  two features plus a line of symmetry, etc. The schema is
   *  intentionally permissive (an array) so adding new kinds
   *  doesn't require new fields. */
  refs: ConstraintRef[];
}

/** One entry in the interleaved top-level ordering of the construction
 *  section (#887): either a top-level construction group or an
 *  ungrouped (loose) construction line. Tracing images are excluded —
 *  ungrouped images render in the decoration section, not here. */
export type TopLevelConstructionRef =
  | { kind: "group"; id: string }
  | { kind: "line"; id: string };

export interface Project {
  id: string;
  name: string;
  pieces: Piece[];
  userStamps: UserStamp[];
  settings: ProjectSettings;
  /** Project-wide stitching defaults — top-level so they live alongside
   *  `pieces` rather than nested under `settings` (which is reserved for
   *  units / theme). Parts and individual stitch lines inherit from here
   *  unless they declare their own override. Optional only for backward
   *  compatibility; the migrator fills it in for older files. */
  stitchingDefaults?: StitchingDefaults;
  /** Construction (guide) lines live at the project level — they're
   *  layout helpers that don't belong to any single part and can be
   *  drawn freely on bare canvas. Optional for backward compatibility
   *  with files saved before the Construction tool shipped. */
  constructionLines?: ConstructionLine[];
  /** Geometric constraints applied to construction-line features.
   *  Optional for backward compatibility with files saved before the
   *  constraint system shipped. Empty array and undefined are
   *  equivalent (no constraints). */
  constraints?: Constraint[];
  /** Named groupings of construction lines (issue #80). Optional for
   *  backward compatibility — files saved before grouping shipped
   *  treat the field as empty. Invariant enforced by
   *  `projectOps.ts`: each construction line id appears in at most
   *  one group's `lineIds`, and groups never have empty `lineIds`
   *  (empty groups get pruned by the helpers). */
  constructionGroups?: ConstructionGroup[];
  /** Interleaved ordering of the construction section's top level
   *  (#887): a single ordered sequence mixing top-level groups and
   *  ungrouped (loose) construction lines so a loose line can sit
   *  above, below, or between groups instead of the two kinds rendering
   *  as separate "groups then lines" blocks. Reconciled on every read by
   *  `topLevelConstructionOrder` — stale / no-longer-top-level entries
   *  are dropped and any current top-level entry missing from the stored
   *  order is appended in the legacy default order — so ordinary
   *  mutations (draw / delete / group / ungroup) don't have to maintain
   *  it. Absent on files saved before #887, in which case the resolver
   *  falls back to the legacy "top-level groups, then loose lines" order,
   *  leaving old projects untouched. */
  constructionTopLevelOrder?: TopLevelConstructionRef[];
  /** Photos imported through the trace-an-image flow (issues #75/#489).
   *  They render below every other canvas element as references to draw
   *  over. Optional for backward compatibility with files saved before
   *  the tracing feature shipped. */
  tracingImages?: TracingImage[];
  /** Matched stitch-line pairs (#199): two seams that share a hole count so
   *  they line up hole-for-hole when sewn together, even across pieces of
   *  different length. Maintained by `deriveStitchMatches`; optional for
   *  backward compatibility (absent = no matches). */
  stitchMatches?: StitchMatch[];
  /** Persisted placement of the auto-sized print mat (#785 follow-up). The
   *  mat normally auto-fits the parts' bounding box, but its position is
   *  stateful during a session so growing it doesn't shove parts off their
   *  grid lines. Saving that placement here means a reload restores the exact
   *  mat the user was working on — parts keep their spot ON the mat instead of
   *  the mat re-centring on them. Absent on older files / empty projects, in
   *  which case the mat auto-sizes from scratch. */
  matLayout?: MatLayout;
  /** Written notes placed on the canvas (#157) — "fold here", "grain runs this
   *  way", whatever the maker needs to tell themselves later. Project-level
   *  rather than per-piece because a note can float free of any part, and the
   *  ones that DO point at a part reference it rather than belonging to it.
   *  Optional for backward compatibility (absent = no notes). */
  notes?: Note[];
  modified: number;
}

/**
 * Where a note's callout line lands on a part (#157).
 *
 * Deliberately NOT an absolute point. Piece geometry is stored already
 * transformed — `translatePiece` / `rotatePiece` / `mirrorPiece` /
 * `transformPieceByAffine` rewrite `shapePathData` outright — and they are
 * called from six different places (SelectTool, the slice and mirror
 * controllers, clipboard, duplicate, the panel's numeric transform). An
 * absolute anchor would have to be re-mapped at every one of those, and the
 * first site anyone forgot would silently leave a callout pointing at where the
 * part used to be.
 *
 * So the anchor is DERIVED from the part's current outline instead: `t` is the
 * normalized distance along `shapePathData` (0–1) of the nearest outline point,
 * and `offsetMm` is the signed distance from there along the outward normal —
 * negative when the maker pointed at something inside the part, which is the
 * common case ("emboss the logo HERE"). Every transform moves the outline, so
 * the anchor follows for free and cannot go stale.
 */
export interface NoteAnchor {
  /**
   * The part that OWNS whatever this callout points at — always set, even when
   * the callout rides a cutout or a seam rather than the part's own silhouette.
   *
   * Kept as the owner rather than being replaced by the finer id because
   * everything that treats a note as belonging somewhere — the printed notes
   * page, and carrying tips along while the leather is dragged — reasons about
   * parts, and a cutout or a seam moves with its part anyway.
   */
  pieceId: string;
  /** When set, the callout rides this CUTOUT's outline instead of the part's
   *  own (#1076). At most one of this and {@link stitchId} is ever set. */
  cutoutId?: string;
  /** When set, the callout rides this SEAM's path instead of the part's own
   *  outline (#1076). */
  stitchId?: string;
  /** Normalized position along the outline being ridden, 0–1. */
  t: number;
  /** Signed distance from the outline along its outward normal, in project mm.
   *  Negative points into the part. */
  offsetMm: number;
}

/**
 * One note on the canvas (#157).
 *
 * The BOX is positioned in absolute project space even when the note is
 * anchored to a part: attaching pins the callout's tip to the part, not the
 * text. Move the part and the tip rides along while the box holds its spot, so
 * notes stay where the maker parked them (typically out in a margin) instead of
 * being dragged over the pattern.
 */
export interface Note {
  id: string;
  /** Note body. Plain multi-line text — wrapped to `widthMm` when drawn. */
  text: string;
  /** Top-left of the note box, in project mm. */
  position: Vec2;
  /** Box size in project mm. Text wraps to this width; the height is what the
   *  maker dragged, independent of how much text there is. */
  widthMm: number;
  heightMm: number;
  /** Cap height of the text in project mm, so a note prints at the size it was
   *  set to — this canvas is true to scale, and a pixel size would come out
   *  different at every zoom. */
  fontSizeMm: number;
  /**
   * Everything this note points at — one callout line each. Absent or empty
   * for a free-floating note, which draws no leader at all.
   *
   * A list rather than a single anchor because one instruction routinely
   * covers several things ("skive both of these to 0.8mm"), and saying it
   * twice makes two notes to keep in step. At most one anchor per TARGET —
   * a part, one of its cutouts, one of its seams — since a second line to the
   * same target says nothing the first does not. A note may point at a part
   * AND at that part's cutout: those are two different things to say something
   * about.
   */
  anchors?: NoteAnchor[];
  /** @deprecated Single-anchor form written by earlier builds. Read through
   *  `noteAnchors()`, never written — kept only so a project autosaved before
   *  the list existed still shows its callout. */
  anchor?: NoteAnchor;
  /** Hidden individually from the notes list, independently of the global
   *  show/hide toggle. Absent = visible. */
  visible?: boolean;
}

/** Size + centre of the print mat, in project mm. See `Project.matLayout`. */
export interface MatLayout {
  w: number;
  h: number;
  cx: number;
  cy: number;
}

/** A reference to one stitch line, addressed by its parent piece (a matched
 *  pair can span two different pieces). */
export interface StitchRef {
  pieceId: string;
  stitchId: string;
}

/** A 1:1 directional match between two stitch lines (#199). The `main` seam
 *  keeps full control of its own chisel settings; the `follower` adopts the
 *  main's current hole count, spread evenly along its own length (so spacing
 *  differs by length / curvature). `deriveStitchMatches` stamps the derived
 *  `holeCount` (+ `matchId`) onto the follower on every commit — so changing
 *  the main's pitch re-stitches the follower — and prunes the record when
 *  either member is gone. */
export interface StitchMatch {
  id: string;
  main: StitchRef;
  follower: StitchRef;
  /** Snapshot of the follower's own chisel style, taken when the match was
   *  created (#199). While matched the follower ADOPTS the main's hole style /
   *  size (so the two seams look identical, overriding the follower's part
   *  defaults); Break restores this snapshot. */
  followerStyle?: {
    shape: StitchShape;
    size: number;
    slantAngle: number;
    pitchMode?: StitchPitchMode;
    overridesDefaults?: boolean;
  };
}

/** A photo placed on the canvas as a tracing reference (issue #489).
 *  The original upload stays in image storage; the de-skew + crop the
 *  user picked in the trace dialog is stored as the crop window's
 *  preimage quad, normalized to the source image (0..1), so the warp
 *  can be re-derived at any resolution on load. */
export interface TracingImage {
  id: string;
  name: string;
  /** Source of the original photo. When the user is signed in this is the
   *  same-origin uploaded URL (`/api/images/<key>`); when not (issue #496)
   *  it's an embedded `data:` URL carrying the (downscaled) photo bytes, so
   *  the image persists with the project instead of on the server. Either
   *  way it loads through `fetch(url)` in `renderTracingImageCanvas`. */
  url: string;
  /** Corners of the region shown on canvas — the crop window's preimage
   *  under the keystone map — in source-image coords normalized to 0..1,
   *  TL, TR, BR, BL order. */
  quad: { x: number; y: number }[];
  /** Physical size of the placed image, mm (from the size calibration). */
  widthMm: number;
  heightMm: number;
  /** Canvas position of the image's top-left corner, mm. */
  x: number;
  y: number;
  visible?: boolean;
  /** On-canvas opacity of the placed reference, 0..1 (issue #516).
   *  Lets the user fade the photo so traced geometry on top reads
   *  clearly. New images start at `DEFAULT_TRACING_IMAGE_OPACITY`
   *  (0.75); absent on files saved before opacity shipped, treated as
   *  that default by `tracingImageOpacity`. */
  opacity?: number;
  /** On-canvas rotation of the placed reference, degrees clockwise
   *  about the image's centre (issue #517). The stored `x`/`y` +
   *  `widthMm`/`heightMm` still describe the un-rotated rectangle; the
   *  rotation is applied around its centre at render / hit-test time.
   *  Absent → 0. Normalised to (-180, 180] by `tracingImageRotation`. */
  rotation?: number;
  /** When true the placed reference is mirrored horizontally in its own
   *  (pre-rotation) frame (issue #545 — the Flip buttons). Applied as a
   *  render-time reflection of the raster about its centre, so it works
   *  regardless of whether the bitmap came from the warp or the stored
   *  `finalUrl` copy. Combined with `rotation` it expresses every flip:
   *  a vertical flip is a horizontal mirror plus a 180° turn. Absent →
   *  not mirrored. The placed rectangle (`x`/`y`/`widthMm`/`heightMm`)
   *  and the source `quad` are unchanged by it. */
  mirrored?: boolean;
  /** User-positioned rotation pivot in project mm (issue #420, extended to
   *  images under #545) — the point the rotation knob spins the image
   *  about. Absent → the image's centre (`tracingImageCenter`). The same
   *  draggable pivot crosshair construction lines and groups carry;
   *  `effectiveImagePivot` resolves this to centre when unset. Rides along
   *  on move / flip / rotate so it stays attached to the image. */
  pivotPoint?: Vec2;
  /** URL of the rendered final copy stored next to the original (issue
   *  #500): the de-skewed + cropped region at 200 DPI, AVIF where the
   *  browser can encode it (PNG fallback otherwise). The on-canvas
   *  reference is still re-derived from `url` + `quad`, so this is for
   *  export / re-use; absent until the background render lands, and on
   *  files where the image was kept local (#496) or the render failed. */
  finalUrl?: string;
}

/** Default on-canvas opacity for a freshly placed tracing image
 *  (issue #516) — faded enough that traced geometry reads clearly on
 *  top, opaque enough to still see the reference. */
export const DEFAULT_TRACING_IMAGE_OPACITY = 0.75;

/** Resolve a tracing image's effective on-canvas opacity, clamped to
 *  0..1, falling back to the default for files saved before opacity
 *  shipped (issue #516). */
export function tracingImageOpacity(image: TracingImage): number {
  const o = image.opacity;
  if (o === undefined || Number.isNaN(o)) return DEFAULT_TRACING_IMAGE_OPACITY;
  return Math.max(0, Math.min(1, o));
}

/** Resolve a tracing image's on-canvas rotation (issue #517), degrees
 *  clockwise about its centre, normalised to (-180, 180]. Falls back
 *  to 0 for files saved before rotation shipped. */
export function tracingImageRotation(image: TracingImage): number {
  const r = image.rotation;
  if (r === undefined || Number.isNaN(r)) return 0;
  let n = r % 360;
  if (n > 180) n -= 360;
  if (n <= -180) n += 360;
  return n;
}

/** The centre of a tracing image's placed rectangle in project mm —
 *  the pivot its rotation (issue #517) is applied about. */
export function tracingImageCenter(image: TracingImage): {
  x: number;
  y: number;
} {
  return {
    x: image.x + image.widthMm / 2,
    y: image.y + image.heightMm / 2,
  };
}

/** Axis-aligned bounding box (project mm) of a tracing image's placed
 *  rectangle, accounting for its rotation (issue #517) — the AABB of
 *  the four rotated corners about the image centre. Matches how a
 *  rotated construction line reports `item.bounds`, so the two read
 *  consistently in arrange / transform geometry. */
export function tracingImageBounds(image: TracingImage): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const deg = tracingImageRotation(image);
  if (deg === 0) {
    return {
      x: image.x,
      y: image.y,
      width: image.widthMm,
      height: image.heightMm,
    };
  }
  const c = tracingImageCenter(image);
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const corners: ReadonlyArray<readonly [number, number]> = [
    [image.x, image.y],
    [image.x + image.widthMm, image.y],
    [image.x + image.widthMm, image.y + image.heightMm],
    [image.x, image.y + image.heightMm],
  ];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [px, py] of corners) {
    const rx = c.x + (px - c.x) * cos - (py - c.y) * sin;
    const ry = c.y + (px - c.x) * sin + (py - c.y) * cos;
    minX = Math.min(minX, rx);
    minY = Math.min(minY, ry);
    maxX = Math.max(maxX, rx);
    maxY = Math.max(maxY, ry);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** True when `pt` (project mm) falls inside the image's placed
 *  rectangle, accounting for its rotation (issue #517) — the point is
 *  rotated back into the image's un-rotated local frame before the
 *  axis-aligned containment test. */
export function tracingImageContainsPoint(
  image: TracingImage,
  pt: { x: number; y: number },
): boolean {
  const c = tracingImageCenter(image);
  const deg = tracingImageRotation(image);
  let lx = pt.x;
  let ly = pt.y;
  if (deg !== 0) {
    const rad = (-deg * Math.PI) / 180;
    const dx = pt.x - c.x;
    const dy = pt.y - c.y;
    lx = c.x + dx * Math.cos(rad) - dy * Math.sin(rad);
    ly = c.y + dx * Math.sin(rad) + dy * Math.cos(rad);
  }
  return (
    lx >= image.x &&
    lx <= image.x + image.widthMm &&
    ly >= image.y &&
    ly <= image.y + image.heightMm
  );
}

/** Normalizer for the optional collection — undefined means "none". */
export function projectTracingImages(project: Project): TracingImage[] {
  return project.tracingImages ?? [];
}

/** Hardcoded fallback used when neither the project, the part, nor the line
 *  has stored stitching defaults. Picks the most-common diagonal-iron
 *  parameters (3.38 mm pitch, 1.8 mm tooth width, regular 45° slant) with
 *  center-to-center pitch measurement. */
export const FALLBACK_STITCHING_DEFAULTS: StitchingDefaults = {
  shape: "slant",
  pitch: 3.38,
  size: 1.8,
  slantAngle: -45,
  pitchMode: "center",
};

/** Resolve a part's effective stitching defaults — its own block if set,
 *  else the project's, else the hardcoded fallback. */
export function resolvePartStitchingDefaults(
  project: Project,
  piece: Piece,
): StitchingDefaults {
  return (
    piece.stitchingDefaults ??
    project.stitchingDefaults ??
    FALLBACK_STITCHING_DEFAULTS
  );
}

/** Project-level resolver — used when no part is in scope (e.g., editing
 *  the project block directly). */
export function resolveProjectStitchingDefaults(
  project: Project,
): StitchingDefaults {
  return project.stitchingDefaults ?? FALLBACK_STITCHING_DEFAULTS;
}

export function createEmptyProject(): Project {
  return {
    id: newId("proj"),
    name: "Untitled",
    pieces: [],
    userStamps: [],
    settings: { units: "mm", background: "dark" },
    stitchingDefaults: { ...FALLBACK_STITCHING_DEFAULTS },
    modified: Date.now(),
  };
}

export function createPiece(name: string, shapePathData: string): Piece {
  return {
    id: newId("piece"),
    name,
    shapePathData,
    holes: [],
    cutouts: [],
    stitchLines: [],
    decorations: [],
  };
}

/** Stitch lines arrived after the initial schema; older saved projects don't
 *  have the field. Always go through this when reading so callers can iterate
 *  without an undefined check. */
/** Walk up an offset chain to the root non-derived construction
 *  line. Returns the input id when the line isn't derived. Used
 *  by the lineDrag pipeline so dragging an offset line redirects
 *  to the root source — the rigid translation then carries every
 *  transitive derived (via `Scene.translateOffsetsOf`) and every
 *  snap-chain partner (via `liveChainTranslate`) along for free.
 *
 *  Defensive against `offsetSource` cycles (which the deriver
 *  doesn't allow but a malformed import could plant): caps the
 *  walk at 1000 steps and returns the last visited id. */
export function resolveOffsetRoot(
  project: Project,
  constructionId: string,
): string {
  const lines = projectConstructionLines(project);
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const seen = new Set<string>();
  let current = constructionId;
  for (let i = 0; i < 1000; i++) {
    if (seen.has(current)) return current;
    seen.add(current);
    const line = byId.get(current);
    if (!line || !line.offsetSource) return current;
    current = line.offsetSource.sourceId;
  }
  return current;
}

export function pieceStitchLines(piece: Piece): StitchLine[] {
  return piece.stitchLines ?? [];
}

/** Find the piece that owns a stitch line by its (globally unique) id, or null.
 *  Stitch matching (#199) selects seams across pieces, so callers that have only
 *  a stitch id need this to resolve its parent part. */
export function pieceForStitchLine(
  project: Project,
  stitchId: string,
): Piece | null {
  for (const piece of project.pieces) {
    if (pieceStitchLines(piece).some((l) => l.id === stitchId)) return piece;
  }
  return null;
}

/** Same pattern as `pieceStitchLines` — `hardware` is optional for
 *  backward compatibility, this helper keeps callers from peppering
 *  null checks. */
export function pieceHardware(piece: Piece): Hardware[] {
  return piece.hardware ?? [];
}

/** Construction lines live on the project (not on individual pieces) so
 *  they can be drawn anywhere on the canvas — over a part, between
 *  parts, or before any parts exist. Optional for backward compat. */
export function projectConstructionLines(project: Project): ConstructionLine[] {
  return project.constructionLines ?? [];
}

/** Construction-line groupings. Optional on the project for backward
 *  compat — this helper normalizes to an empty array so callers can
 *  iterate without an undefined check. */
export function projectConstructionGroups(
  project: Project,
): ConstructionGroup[] {
  return project.constructionGroups ?? [];
}

/** Matched stitch-line pairs (#199). Optional on the project for backward
 *  compat — normalizes to an empty array so callers can iterate freely. */
export function projectStitchMatches(project: Project): StitchMatch[] {
  return project.stitchMatches ?? [];
}

/** Find the group that owns a given construction line, or null when the
 *  line is ungrouped. Linear scan is fine — groups are small in
 *  practice and the layers panel only calls this during a render. */
export function groupForConstructionLine(
  project: Project,
  lineId: string,
): ConstructionGroup | null {
  for (const g of projectConstructionGroups(project)) {
    if (g.lineIds.includes(lineId)) return g;
  }
  return null;
}

/** Find the group that owns a given tracing image, or null when the
 *  image is ungrouped (issue #509). Image analogue of
 *  `groupForConstructionLine`. */
export function groupForTracingImage(
  project: Project,
  imageId: string,
): ConstructionGroup | null {
  for (const g of projectConstructionGroups(project)) {
    if ((g.imageIds ?? []).includes(imageId)) return g;
  }
  return null;
}

/** Auto-numbered "Group N" name. Same idea as `nextPartName`: scans
 *  existing groups for the pattern, returns one past the max, so
 *  renamed groups don't collide and deleted numbers aren't reused. */
export function nextConstructionGroupName(project: Project): string {
  let max = 0;
  for (const g of projectConstructionGroups(project)) {
    const m = /^Group (\d+)$/.exec(g.name);
    if (m) {
      const n = parseInt(m[1]!, 10);
      if (n > max) max = n;
    }
  }
  return `Group ${max + 1}`;
}

/** Construction groups that aren't a child of any other group. The
 *  layers panel renders these as the top-level group rows; nested
 *  groups are rendered recursively under their parent. */
export function topLevelConstructionGroups(
  project: Project,
): ConstructionGroup[] {
  const groups = projectConstructionGroups(project);
  const childIds = new Set<string>();
  for (const g of groups) {
    for (const childId of g.groupIds ?? []) childIds.add(childId);
  }
  return groups.filter((g) => !childIds.has(g.id));
}

/** Resolve the single interleaved ordering of the construction
 *  section's top level (#887): top-level groups and ungrouped
 *  construction lines share ONE ordered sequence so a loose line can
 *  sit above, below, or between groups.
 *
 *  `project.constructionTopLevelOrder` is the stored intent, but it's
 *  reconciled on every read so ordinary mutations don't have to keep it
 *  in sync: entries that are no longer top-level (deleted, or pulled
 *  into a group) are dropped, and any current top-level entry missing
 *  from the stored order is appended in the legacy default order —
 *  top-level groups (in `constructionGroups` order) first, then
 *  ungrouped lines (in `constructionLines` order). A project with no
 *  stored order (every file saved before #887) therefore resolves to
 *  exactly the legacy layout: groups pinned above loose lines. */
export function topLevelConstructionOrder(
  project: Project,
): TopLevelConstructionRef[] {
  const groups = topLevelConstructionGroups(project);
  const groupIdSet = new Set(groups.map((g) => g.id));
  const groupedLineIds = new Set<string>();
  for (const g of projectConstructionGroups(project)) {
    for (const id of g.lineIds) groupedLineIds.add(id);
  }
  const looseLines = projectConstructionLines(project).filter(
    (l) => !groupedLineIds.has(l.id),
  );
  const looseLineIdSet = new Set(looseLines.map((l) => l.id));
  const defaultOrder: TopLevelConstructionRef[] = [
    ...groups.map((g) => ({ kind: "group" as const, id: g.id })),
    ...looseLines.map((l) => ({ kind: "line" as const, id: l.id })),
  ];
  const isValid = (ref: TopLevelConstructionRef): boolean =>
    ref.kind === "group" ? groupIdSet.has(ref.id) : looseLineIdSet.has(ref.id);
  const result: TopLevelConstructionRef[] = [];
  const seen = new Set<string>();
  for (const ref of project.constructionTopLevelOrder ?? []) {
    const key = `${ref.kind}:${ref.id}`;
    if (seen.has(key) || !isValid(ref)) continue;
    seen.add(key);
    result.push(
      ref.kind === "group"
        ? { kind: "group", id: ref.id }
        : { kind: "line", id: ref.id },
    );
  }
  // Append any current top-level entry not covered by the stored order,
  // in the legacy groups-then-loose-lines default.
  for (const ref of defaultOrder) {
    const key = `${ref.kind}:${ref.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(ref);
  }
  return result;
}

/** Walks the parent chain of a construction group, top-down — so
 *  `groupAncestorIds(g)` returns `[great-grand-parent, …, parent]`
 *  with `g` itself excluded. Used by cycle checks before moving a
 *  group into a new parent. Linear scan per step; groups are small
 *  in practice so the quadratic worst case doesn't bite. */
export function groupAncestorIds(project: Project, groupId: string): string[] {
  const groups = projectConstructionGroups(project);
  const parentOf = new Map<string, string>();
  for (const g of groups) {
    for (const childId of g.groupIds ?? []) parentOf.set(childId, g.id);
  }
  const out: string[] = [];
  let cur = parentOf.get(groupId);
  const seen = new Set<string>();
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    out.unshift(cur);
    cur = parentOf.get(cur);
  }
  return out;
}

/** Top-down ancestor chain of a construction line — starts at the
 *  topmost ancestor group and ends with the line's direct parent
 *  group. Empty when the line is at top level (no parent group).
 *  Mirrors `groupAncestorIds` but indexes lines via `lineIds`
 *  membership instead of `groupIds`. */
export function lineAncestorIds(project: Project, lineId: string): string[] {
  const groups = projectConstructionGroups(project);
  let directParent: string | undefined;
  for (const g of groups) {
    if (g.lineIds.includes(lineId)) {
      directParent = g.id;
      break;
    }
  }
  if (!directParent) return [];
  return [...groupAncestorIds(project, directParent), directParent];
}

/** Every line id reachable from a group by walking its `lineIds`
 *  plus recursively the `lineIds` of its descendants. Used when
 *  selecting a group as an entity — the canvas highlight wants the
 *  full set of leaf lines, not just the direct members of the
 *  clicked group. */
export function descendantConstructionLineIds(
  project: Project,
  groupId: string,
): string[] {
  const groups = projectConstructionGroups(project);
  const byId = new Map(groups.map((g) => [g.id, g] as const));
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (gid: string) => {
    if (seen.has(gid)) return; // Defensive — model invariant forbids
    seen.add(gid); //   cycles but a corrupted import shouldn't loop.
    const g = byId.get(gid);
    if (!g) return;
    for (const lineId of g.lineIds) out.push(lineId);
    for (const childId of g.groupIds ?? []) walk(childId);
  };
  walk(groupId);
  return out;
}

/** Top-down ancestor chain of a tracing image — image analogue of
 *  `lineAncestorIds`. Empty when the image is ungrouped. */
export function imageAncestorIds(project: Project, imageId: string): string[] {
  const groups = projectConstructionGroups(project);
  let directParent: string | undefined;
  for (const g of groups) {
    if ((g.imageIds ?? []).includes(imageId)) {
      directParent = g.id;
      break;
    }
  }
  if (!directParent) return [];
  return [...groupAncestorIds(project, directParent), directParent];
}

/** Every tracing-image id reachable from a group by walking its
 *  `imageIds` plus recursively its descendant groups' `imageIds`
 *  (issue #509). Image analogue of `descendantConstructionLineIds` —
 *  used to highlight / drag every image inside a selected group. */
export function descendantConstructionImageIds(
  project: Project,
  groupId: string,
): string[] {
  const groups = projectConstructionGroups(project);
  const byId = new Map(groups.map((g) => [g.id, g] as const));
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (gid: string) => {
    if (seen.has(gid)) return;
    seen.add(gid);
    const g = byId.get(gid);
    if (!g) return;
    for (const imageId of g.imageIds ?? []) out.push(imageId);
    for (const childId of g.groupIds ?? []) walk(childId);
  };
  walk(groupId);
  return out;
}

/** Returns every construction group whose *entire* descendant member
 *  set — lines AND images (issue #509) — is contained in the selected
 *  sets. Used by selection paths (lasso, modifier-clicks, the Cmd+G
 *  handler) to auto-promote a group into the entity selection when the
 *  user has selected all of its members. Empty groups (no descendants
 *  of either kind) are not promoted; they have no membership to
 *  compare against. */
export function fullySelectedConstructionGroupIds(
  project: Project,
  selectedLineIds: Iterable<string>,
  selectedImageIds: Iterable<string> = [],
): string[] {
  const lineSet = new Set(selectedLineIds);
  const imageSet = new Set(selectedImageIds);
  if (lineSet.size === 0 && imageSet.size === 0) return [];
  const out: string[] = [];
  for (const g of projectConstructionGroups(project)) {
    const lines = descendantConstructionLineIds(project, g.id);
    const images = descendantConstructionImageIds(project, g.id);
    if (lines.length === 0 && images.length === 0) continue;
    let all = true;
    for (const lid of lines) {
      if (!lineSet.has(lid)) {
        all = false;
        break;
      }
    }
    if (all) {
      for (const iid of images) {
        if (!imageSet.has(iid)) {
          all = false;
          break;
        }
      }
    }
    if (all) out.push(g.id);
  }
  return out;
}

/** Every group id reachable from `groupId` by walking its `groupIds`
 *  recursively, including `groupId` itself. Used when deleting a
 *  construction group with subgroups — the delete path needs to wipe
 *  every group record in the subtree, not just the entry-point. */
export function descendantConstructionGroupIds(
  project: Project,
  groupId: string,
): string[] {
  const groups = projectConstructionGroups(project);
  const byId = new Map(groups.map((g) => [g.id, g] as const));
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (gid: string) => {
    if (seen.has(gid)) return;
    seen.add(gid);
    const g = byId.get(gid);
    if (!g) return;
    out.push(gid);
    for (const childId of g.groupIds ?? []) walk(childId);
  };
  walk(groupId);
  return out;
}

/** Picks the next "Stitch N" name, numbered GLOBALLY across every part (not
 *  per-part) so each seam gets a distinct number — a construction-edge trace
 *  that fans one seam out to several parts then reads "Stitch 4 / 5 / 6" rather
 *  than "Stitch 1" three times over. `offset` reserves room when several seams
 *  are created in one commit (project isn't re-scanned between them): pass the
 *  seam's index so the batch numbers run consecutively. */
export function nextStitchName(project: Project, offset = 0): string {
  let max = 0;
  for (const piece of project.pieces) {
    for (const s of pieceStitchLines(piece)) {
      const m = /^Stitch (\d+)$/.exec(s.name ?? "");
      if (m) {
        const n = parseInt(m[1]!, 10);
        if (n > max) max = n;
      }
    }
  }
  return `Stitch ${max + 1 + offset}`;
}

/** Modifiers `nextConstructionName` knows about — used by
 *  `isAutoGeneratedConstructionName` to detect names the system gave
 *  out (so they can be cleaned up when primitive metadata is dropped)
 *  vs. names a user typed by hand (which should stay untouched). */
const KNOWN_CONSTRUCTION_MODIFIERS = [
  "Rectangle",
  "Square",
  "Ellipse",
  "Circle",
  "Pill",
  "Line",
  "Polyline",
  "Polygon",
  "Triangle",
  "Pentagon",
  "Hexagon",
  "Octagon",
  "Curve",
];

/** Shape names for the well-known regular polygons: a freshly created
 *  equilateral 3/4/5/6/8-gon is named "Triangle" / "Square" / "Pentagon" /
 *  "Hexagon" / "Octagon" instead of the generic "Polygon". Other counts
 *  return null (the caller falls back to "Polygon"). These names count as
 *  in-bucket for polygon-kind lines in `deriveConstructionLineKinds`, so
 *  they survive derive passes but still rename when the shape stops being
 *  a polygon. */
export const REGULAR_POLYGON_SHAPE_MODIFIERS = [
  "Triangle",
  "Square",
  "Pentagon",
  "Hexagon",
  "Octagon",
];

export function regularPolygonModifier(sides: number): string | null {
  switch (sides) {
    case 3:
      return "Triangle";
    case 4:
      return "Square";
    case 5:
      return "Pentagon";
    case 6:
      return "Hexagon";
    case 8:
      return "Octagon";
    default:
      return null;
  }
}

/** True when `name` looks like an auto-generated construction-line
 *  name: bare "Construction N", "Construction - <Modifier> N" (the
 *  legacy prefixed format, kept so saved files keep working), or
 *  the bare-modifier form "<Modifier> N" the system now emits for
 *  every modifier. Used to decide whether to rename the line when
 *  its primitive metadata is dropped — auto names get cleaned up;
 *  hand-edited names stay. */
export function isAutoGeneratedConstructionName(
  name: string | undefined,
): boolean {
  if (!name) return true;
  if (/^Construction \d+$/.test(name)) return true;
  for (const mod of KNOWN_CONSTRUCTION_MODIFIERS) {
    if (new RegExp(`^Construction - ${mod} \\d+$`).test(name)) return true;
    if (new RegExp(`^${mod} \\d+$`).test(name)) return true;
  }
  return false;
}

/** Picks the next available construction-line name.
 *  - No modifier → "Construction N" (legacy unnamed default — the
 *    bare word is the only label that fits when there's no
 *    modifier to describe the line).
 *  - Any modifier (Square / Polygon / Curve / …) → bare
 *    "<Modifier> N". The "Construction - " prefix used to be
 *    glued in front of every modifier; it's redundant noise next
 *    to a self-describing word, so we drop it for new names
 *    across the board.
 *  Each modifier has its own counter; the search spans BOTH the
 *  legacy "Construction - Polygon N" and the new bare "Polygon N"
 *  forms so the sequence stays contiguous in projects opened from
 *  older files. */
export function nextConstructionName(
  project: Project,
  modifier?: string,
): string {
  const escaped = modifier
    ? modifier.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    : null;
  const patterns: RegExp[] = escaped
    ? [
        new RegExp(`^${escaped} (\\d+)$`),
        new RegExp(`^Construction - ${escaped} (\\d+)$`),
      ]
    : [/^Construction (\d+)$/];
  let max = 0;
  for (const c of projectConstructionLines(project)) {
    for (const pattern of patterns) {
      const m = pattern.exec(c.name ?? "");
      if (m) {
        const n = parseInt(m[1]!, 10);
        if (n > max) max = n;
      }
    }
  }
  return modifier ? `${modifier} ${max + 1}` : `Construction ${max + 1}`;
}

/** Pick a duplicate's name from the ORIGINAL's name rather than
 *  re-deriving it from the primitive / kind (issue #382). A trailing
 *  " N" counter is stripped off the original to get a base, and the
 *  next free "<base> N" is returned — so a custom "Door Panel" becomes
 *  "Door Panel 2" and an auto-named "Square 3" becomes "Square 4"
 *  (instead of resetting to the primitive's "Rectangle 1"). The
 *  original counts as the family's first, so the copy always lands at
 *  least at "<base> 2". `existing` carries every name already in use —
 *  including copies minted earlier in the same commit — so a repeated
 *  duplicate stays collision-free. Falls back to `fallback()` when the
 *  original has no usable name. */
function nextNameFromOriginal(
  existing: Iterable<string>,
  originalName: string | undefined,
  fallback: () => string,
): string {
  const trimmed = (originalName ?? "").trim();
  if (!trimmed) return fallback();
  const base = trimmed.replace(/\s+\d+$/, "").trim() || trimmed;
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const numbered = new RegExp(`^${escaped} (\\d+)$`);
  // The original itself (bare base or already numbered) is the first in
  // the family, so the copy starts no lower than "<base> 2".
  let max = 1;
  for (const name of existing) {
    const m = numbered.exec(name);
    if (m) {
      const n = parseInt(m[1]!, 10);
      if (n > max) max = n;
    }
  }
  return `${base} ${max + 1}`;
}

/** Duplicate-naming for construction lines (issue #382): derive the
 *  copy's name from `originalName`, numbering within that name's
 *  family. Falls back to the kind-agnostic auto-name when the source
 *  had no name. */
export function nextDuplicateConstructionName(
  project: Project,
  originalName: string | undefined,
): string {
  const existing = projectConstructionLines(project)
    .map((l) => l.name)
    .filter((n): n is string => typeof n === "string");
  return nextNameFromOriginal(existing, originalName, () =>
    nextConstructionName(project, undefined),
  );
}

/** Duplicate-naming for construction groups (issue #382): derive the
 *  copy's name from the original group's name instead of always
 *  minting a fresh "Group N". */
export function nextDuplicateConstructionGroupName(
  project: Project,
  originalName: string | undefined,
): string {
  const existing = projectConstructionGroups(project).map((g) => g.name);
  return nextNameFromOriginal(existing, originalName, () =>
    nextConstructionGroupName(project),
  );
}

/** Duplicate-naming for tracing images — derive the copy's name from
 *  the original image's name, numbering within that family (same
 *  convention as lines / groups). */
export function nextDuplicateTracingImageName(
  project: Project,
  originalName: string | undefined,
): string {
  const existing = projectTracingImages(project).map((img) => img.name);
  return nextNameFromOriginal(existing, originalName, () => "Traced image");
}

/** Pick a name for a freshly-created offset construction line:
 *  `<source.name> (offset)`, numeric-suffixed only if that base is
 *  already taken. Borrowing the source's name gives the user a clear
 *  visual pair in the layers panel — "Rectangle 2" / "Rectangle 2
 *  (offset)" instead of decoupled auto-numbered names. */
export function nextConstructionOffsetName(
  project: Project,
  source: ConstructionLine,
): string {
  const base =
    source.name && source.name.length > 0
      ? `${source.name} (offset)`
      : `Construction (offset)`;
  const existing = new Set(
    projectConstructionLines(project)
      .map((l) => l.name)
      .filter((n): n is string => typeof n === "string"),
  );
  if (!existing.has(base)) return base;
  for (let i = 2; i < 1000; i++) {
    const cand = `${base} ${i}`;
    if (!existing.has(cand)) return cand;
  }
  return base;
}

/** Mirror-line analog of `nextConstructionOffsetName` (issue #180).
 *  Produces `<source.name> (mirrored)` — borrows the source's name so
 *  the layers panel reads as a clear pair. Numeric-suffixed only if
 *  the base is already taken. */
export function nextConstructionMirrorName(
  project: Project,
  source: ConstructionLine,
): string {
  const base =
    source.name && source.name.length > 0
      ? `${source.name} (mirrored)`
      : `Construction (mirrored)`;
  const existing = new Set(
    projectConstructionLines(project)
      .map((l) => l.name)
      .filter((n): n is string => typeof n === "string"),
  );
  if (!existing.has(base)) return base;
  for (let i = 2; i < 1000; i++) {
    const cand = `${base} ${i}`;
    if (!existing.has(cand)) return cand;
  }
  return base;
}

/** Picks the next "Cutout N" name not in use within a piece. */
export function nextCutoutName(piece: Piece): string {
  let max = 0;
  for (const c of piece.cutouts) {
    const m = /^Cutout (\d+)$/.exec(c.name ?? "");
    if (m) {
      const n = parseInt(m[1]!, 10);
      if (n > max) max = n;
    }
  }
  return `Cutout ${max + 1}`;
}

/**
 * Picks the next "{baseName} N" name not already in use within a piece's
 * decoration list. Used to auto-number stamps so the panel can group them
 * (e.g. "Basket Weave (Classic) 1", "Basket Weave (Classic) 2", ...). Renamed
 * decorations don't collide and deleted numbers aren't reused.
 */
export function nextStampDecorationName(
  piece: Piece,
  baseName: string,
): string {
  const prefix = baseName + " ";
  let max = 0;
  for (const d of piece.decorations) {
    if (!d.name.startsWith(prefix)) continue;
    const tail = d.name.slice(prefix.length);
    if (!/^\d+$/.test(tail)) continue;
    const n = parseInt(tail, 10);
    if (n > max) max = n;
  }
  return `${baseName} ${max + 1}`;
}

/**
 * Picks the next "Part N" name not already in use. Looks for existing names
 * matching `^Part (\d+)$` and returns one past the max — so renamed parts
 * don't collide and deleted numbers aren't reused.
 */
export function nextPartName(project: Project): string {
  let max = 0;
  for (const p of project.pieces) {
    const m = /^Part (\d+)$/.exec(p.name);
    if (m) {
      const n = parseInt(m[1]!, 10);
      if (n > max) max = n;
    }
  }
  return `Part ${max + 1}`;
}
